// src/services/subscriptionService.ts
import api from './api';
import { ApiResponse, SubscriptionPlan, PublicUserSubscription } from '../types';

export type SubscriberType = "doctor" | "public" | "hospital";
export type SubscriptionStatus = "Active" | "Pending" | "Expired" | "Cancelled";

export interface SubscriptionItem {
  id: string;
  subscriberName: string;
  subscriberType: SubscriberType;
  subscriberId?: string;
  email: string;
  subscriptionPlanId?: string;
  planName: string;
  billingCycle: "Monthly" | "Annual";
  amount: number | string;
  currency: string;
  status: SubscriptionStatus;
  startDate: string;
  endDate: string;
  autoRenew?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  plan?: SubscriptionPlan;
}

export interface SubscriptionListParams {
  search?: string;
  status?: string;
  subscriberType?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface SubscriptionListResponse {
  subscriptions: SubscriptionItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetch public subscription plans (visible + active).
 * Used during registration to display selectable plans.
 * Plans are managed by the Super Admin in the Pf panel.
 * @param platformType - 'doctor' or 'public' to filter plans by platform type
 */
export const getPublicPlans = async (platformType?: 'doctor' | 'public'): Promise<SubscriptionPlan[]> => {
  const params = platformType ? { type: platformType } : {};
  const { data } = await api.get('/subscription-plans/public', { params });
  return Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
};

export const getDoctorSubscriptionPlan = async (): Promise<SubscriptionPlan | null> => {
  const { data } = await api.get<ApiResponse<{ subscriptionPlan: SubscriptionPlan | null }>>(
    '/doctors/me/subscription'
  );
  return data?.data?.subscriptionPlan ?? null;
};

/**
 * Fetch the authenticated public user's own subscription details.
 */
export const getMyPublicSubscription = async (): Promise<PublicUserSubscription | null> => {
  const { data } = await api.get<ApiResponse<{ subscription: PublicUserSubscription | null }>>(
    '/public/me/subscription'
  );
  return data?.data?.subscription ?? null;
};

/**
 * Super Admin: list unified subscriptions with search, filter, pagination
 */
export const listSubscriptions = async (
  params?: SubscriptionListParams
): Promise<SubscriptionListResponse> => {
  const { data } = await api.get('/admin/subscriptions', { params });
  return data?.data ?? data;
};

/**
 * Super Admin: get subscription details by ID
 */
export const getSubscriptionById = async (
  id: string
): Promise<SubscriptionItem> => {
  const { data } = await api.get(`/admin/subscriptions/${id}`);
  return data?.data ?? data;
};

// ── Dashboard Types & API ──────────────────────────────────────────

export interface KpiBreakdown {
  active: number;
  pending: number;
  expired: number;
  cancelled?: number;
}

export interface RevenueBreakdown {
  hospitalRevenue: number;
  hospitalRevenueFormatted: string;
  doctorRevenue: number;
  doctorRevenueFormatted: string;
  publicRevenue: number;
  publicRevenueFormatted: string;
}

export interface KpiWidgetData {
  count: number;
  changeText: string;
  changeType: "positive" | "negative" | "neutral";
  breakdown: KpiBreakdown;
}

export interface RevenueKpiWidgetData {
  amount: number;
  formatted: string;
  changeText: string;
  changeType: "positive" | "negative" | "neutral";
  breakdown: RevenueBreakdown;
}

export interface RevenueTrendItem {
  month: string;
  fullMonth: string;
  doctorRevenue: number;
  publicRevenue: number;
  hospitalRevenue: number;
  totalRevenue: number;
  byPlan?: Record<string, number>;
}

export interface PlanDistributionItem {
  planId: string;
  planName: string;
  hospitals: number;
  doctors: number;
  publicUsers: number;
  total: number;
}

export interface SubscriptionStatusUsage {
  label: string;
  count: number;
  color: string;
  percentage: number;
  byType?: {
    doctor: number;
    public: number;
    hospital?: number;
  };
}

export interface SubscriptionUsageData {
  total: number;
  statuses: SubscriptionStatusUsage[];
}

export interface TopPlanItem {
  planId: string;
  planName: string;
  subscriptions: number;
  totalSubscriptions: number;
  revenue: number;
  revenueFormatted: string;
}

export interface TopPlanMonthlyTrendItem {
  month: string;
  fullMonth: string;
  revenue: number;
  subscriptions: number;
}

export interface SubscriptionDashboardStats {
  dateRange: {
    startDate: string;
    endDate: string;
    label: string;
    preset: string;
  };
  kpis: {
    totalDoctors: KpiWidgetData;
    totalPublicUsers: KpiWidgetData;
    totalSubscriptions: KpiWidgetData;
    totalRevenue: RevenueKpiWidgetData;
  };
  revenueTrend: RevenueTrendItem[];
  planDistribution: PlanDistributionItem[];
  subscriptionUsage: SubscriptionUsageData;
  topPlans: TopPlanItem[];
  topPlanMonthlyTrend?: TopPlanMonthlyTrendItem[];
}

export interface DashboardQueryParams {
  startDate?: string;
  endDate?: string;
  preset?: string;
}

/**
 * Super Admin: get Subscription Dashboard stats
 */
export const getSubscriptionDashboardStats = async (
  params?: DashboardQueryParams
): Promise<SubscriptionDashboardStats> => {
  const { data } = await api.get('/admin/subscriptions/dashboard', { params });
  return data?.data ?? data;
};